import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Avatar from './ui/Avatar';

interface NavLink { to: string; label: string; roles: string[] }

const navLinks: NavLink[] = [
  // Renter
  { to: '/renter/dashboard',  label: 'Dashboard',      roles: ['RENTER'] },
  { to: '/renter/pay-rent',   label: 'Pay Rent',        roles: ['RENTER'] },
  { to: '/maintenance/track', label: 'Maintenance',     roles: ['RENTER'] },
  { to: '/documents',         label: 'Documents',       roles: ['RENTER'] },
  { to: '/messages',          label: 'Messages',        roles: ['RENTER'] },
  { to: '/complaints/new',    label: 'File Complaint',  roles: ['RENTER'] },
  // Owner
  { to: '/owner/dashboard',   label: 'Dashboard',       roles: ['OWNER'] },
  { to: '/owner/properties',  label: 'Properties',      roles: ['OWNER'] },
  { to: '/owner/leases',      label: 'Leases',          roles: ['OWNER'] },
  { to: '/owner/ledger',      label: 'Rent Ledger',     roles: ['OWNER'] },
  { to: '/owner/tax-reports', label: 'Tax Reports',     roles: ['OWNER'] },
  { to: '/owner/compliance',  label: 'Compliance',      roles: ['OWNER'] },
  { to: '/messages',          label: 'Messages',        roles: ['OWNER'] },
  // Agent
  { to: '/agent/dashboard',   label: 'Dashboard',       roles: ['AGENT'] },
  { to: '/agent/properties',  label: 'Properties',      roles: ['AGENT'] },
  { to: '/agent/leases',      label: 'Leases',          roles: ['AGENT'] },
  { to: '/agent/maintenance', label: 'Maintenance',     roles: ['AGENT'] },
  { to: '/agent/inspections', label: 'Inspections',     roles: ['AGENT'] },
  { to: '/messages',          label: 'Messages',        roles: ['AGENT'] },
  // Contractor
  { to: '/contractor/dashboard', label: 'Dashboard',   roles: ['CONTRACTOR'] },
  { to: '/contractor/jobs',      label: 'My Jobs',     roles: ['CONTRACTOR'] },
  // Platform Admin
  { to: '/admin/dashboard',   label: 'Dashboard',       roles: ['ADMIN'] },
  { to: '/admin/agencies',    label: 'Agencies',        roles: ['ADMIN'] },
  { to: '/admin/users',       label: 'Users',           roles: ['ADMIN'] },
  { to: '/admin/properties',  label: 'Properties',      roles: ['ADMIN'] },
  { to: '/admin/settings',    label: 'System Settings', roles: ['ADMIN'] },
  // Agency Admin
  { to: '/agency/dashboard',  label: 'Dashboard',       roles: ['AGENCY_ADMIN'] },
  { to: '/admin/settings',    label: 'Settings',        roles: ['AGENCY_ADMIN'] },
];

const MobileTopBar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { user, role, logout } = useAuth();
  const location = useLocation();
  const effectiveRole = role === 'ADMIN' && user?.agencyId ? 'AGENCY_ADMIN' : role;
  const links = navLinks.filter(l => effectiveRole && l.roles.includes(effectiveRole));

  return (
    <div className="md:hidden flex-none">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 h-14 border-b border-ink-200 bg-card shadow-xs">
        <div className="flex items-center gap-2.5">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-sm flex-none"
            style={{ background: 'var(--forest-900)', color: 'var(--teal-400)' }}
          >
            R
          </div>
          <span className="font-semibold text-ink-900 text-sm">Rentry</span>
        </div>
        <button
          onClick={() => setOpen(v => !v)}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-700"
          style={{ background: 'var(--ink-100)' }}
          aria-label="Menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Drawer overlay */}
      {open && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setOpen(false)}
          />
          <div className="fixed top-0 left-0 h-full w-72 z-50 flex flex-col shadow-lg bg-card">
            {/* Drawer header */}
            <div
              className="flex items-center gap-3 px-4 py-4 border-b border-ink-200"
              style={{ background: 'var(--forest-900)' }}
            >
              <Avatar name={user?.name || user?.email || '?'} size={36} />
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold text-sm truncate">
                  {user?.firstName || user?.name || user?.email}
                </div>
                <div className="text-[11px] mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  {role}
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-white/70 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            {/* Links */}
            <nav className="flex-1 overflow-y-auto py-3 px-2">
              <ul className="space-y-0.5">
                {links.map((link, i) => {
                  const active = location.pathname === link.to || location.pathname.startsWith(link.to + '/');
                  return (
                    <li key={i}>
                      <Link
                        to={link.to}
                        onClick={() => setOpen(false)}
                        className="block px-3 py-2.5 rounded-lg text-[14px] font-medium transition-all"
                        style={{
                          background: active ? 'var(--forest-900)' : 'transparent',
                          color: active ? '#fff' : 'var(--ink-800)',
                        }}
                      >
                        {link.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Logout */}
            <div className="px-3 py-3 border-t border-ink-200">
              <button
                onClick={() => { logout(); setOpen(false); }}
                className="flex items-center gap-2 w-full px-3 py-2.5 rounded-lg text-[14px] font-medium text-ink-700 hover:bg-ink-100"
              >
                <LogOut size={16} />
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MobileTopBar;
